//React
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
//Libraries
import cx from "classnames";
//Components
import {
  Card,
  DocsScreen,
  FlexBox,
  removeLoader,
  showLoader,
  TitleBlock,
  Image,
} from "components";
//Constants
import { docsConstants } from "../../../constants";
//Utils
import {
  ChangeHistory,
  EventManager,
  EventNames,
  useIsInViewport,
} from "utils";
//Styles
import "./styles.scss";
//Assets
import JavaIcon from "assets/icons/java.png";
import AndroidIcon from "assets/icons/android.svg";
import AppleIcon from "assets/icons/apple.svg";
import FlutterIcon from "assets/icons/flutter.png";
import ROUTE_PATH from "Routes/paths";

const DocsHome = () => {
  /* Constants */
  //States
  const [scrolledToElement, setScrolledToElement]: any = useState({
    selectedTab: docsConstants.TITLES.OVERVIEW,
  });

  //Params
  const sdkType: any = useParams();

  //Refs
  const WelcomeSectionRef: any = useRef();
  const isWelcomeSectionInViewport = useIsInViewport(WelcomeSectionRef);
  const GetStartedSectionRef: any = useRef();
  const isGetStartedSectionInViewport = useIsInViewport(GetStartedSectionRef);
  const SdkSectionRef: any = useRef();
  const isSdkSectionInViewport = useIsInViewport(SdkSectionRef);
  const SdkListRef: any = useRef();
  const isSdkListInViewport = useIsInViewport(SdkListRef);

  //Jsons
  const scrollAccToSpecificElementMap = {
    [docsConstants.TITLES.OVERVIEW]: WelcomeSectionRef,
    [docsConstants.TITLES.GET_STARTED_WITH_OTPLESS]: GetStartedSectionRef,
    [docsConstants.TITLES.BACKEND_APIS]: SdkSectionRef,
    [docsConstants.TITLES.SDK_AND_API_REFERENCE]: SdkListRef,
  };

  /* useEffects */
  useEffect(() => {
    EventManager.send(EventNames.EVENTS_NAME.DOCS.HOME_PAGE.PAGE_VIEW);
  }, []);

  useEffect(() => {
    if (isWelcomeSectionInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.OVERVIEW,
      });
    } else if (isGetStartedSectionInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.GET_STARTED_WITH_OTPLESS,
      });
    } else if (isSdkSectionInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.BACKEND_APIS,
      });
    } else if (isSdkListInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    }
  }, [
    isWelcomeSectionInViewport,
    isGetStartedSectionInViewport,
    isSdkSectionInViewport,
    isSdkListInViewport,
  ]);

  /* Functions */
  //Scroll To Specific element
  const scrollToSpecificElement = (selectedTab) => {
    scrollAccToSpecificElementMap[selectedTab]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  //Render UI
  const WelcomeSection = (
    <div ref={WelcomeSectionRef}>
      <TitleBlock
        title={docsConstants.TITLES.OVERVIEW}
        content={
          <p className="fs-16">
            OTPless allows you to enable frictionless and secure{" "}
            <span className="font-heavy"> “Login with WhatsApp”</span> button
            for free. With the flexibility and reliability of OTPless, you can
            use the WhatsApp channel to verify your users mobile numbers without
            OTPs (One Time Passwords).
            <div><b>Please note that this document pertains to 
            our previous API version. If you wish to use it, please  <a href="https://wa.me/917948221313?text=Hello%2C%20I%20have%20a%20question"> <b>contact us</b></a> for access to the old API dashboard. 
            For the latest API version and features, please visit our website at <a href="https://otpless.com">OTPless</a></b> </div>
          </p>
        }
        titleClass="fs-32 font-heavy"
        className="animated fadeInUp mr-b-60"
      />
    </div>
  );

  const GetStartedSection = (
    <div ref={GetStartedSectionRef}>
      <TitleBlock
        title={docsConstants.TITLES.GET_STARTED_WITH_OTPLESS}
        content={
          <div className="fs-16">
            <ul className="decimal">
              <li>
                Create an application
              </li>
              <li>
                Use the API keys (Client ID, Client Secret) to integrate with{" "}
                <span
                  className="underline clicking-animation"
                  onClick={() => {
                    scrollToSpecificElement(docsConstants.TITLES.BACKEND_APIS);
                  }}
                >
                  SDKs or Rest APIs
                </span>
                .
              </li>
              <li> Set the redirection URL.</li>
              <li>
                {" "}
                Test the complete flow using the Test Environment API keys.
              </li>
            </ul>
            <p>
              That’s it. Once this is done, use the Live Environment API keys
              and go live!
            </p>
          </div>
        }
        titleClass="fs-24 font-heavy"
        className=" mr-b-60 animated fadeInUp delay-01s"
      />
    </div>
  );

  const SdkList = (
    <div ref={SdkListRef}>
      <TitleBlock
        title={docsConstants.TITLES.SDK_AND_API_REFERENCE}
        content={
          <div className="sdk-list-section word-break">
            <p className="fs-14">
              OTPless SDKs and APIs allow you to build Login with WhatsApp
              authentication with minimum backend code.
            </p>
            <FlexBox justifyBetween className="mr-t-40 sdk-list text-center">
              <Card
                className="pd-0 w-20 curs-point clicking-animation animated fadeInLeft delay-03s"
                onClick={() => {
                  EventManager.send(
                    EventNames.EVENTS_NAME.DOCS.HOME_PAGE.ANDROID
                  );
                  ChangeHistory("push", ROUTE_PATH.DOCS.SDK.ANDROID);
                }}
              >
                <div className="pd-20">
                  <Image src={AndroidIcon} />
                  <p className="mr-t-20 fs-16 font-heavy">
                    {docsConstants.TITLES.ANDROID.TITLE}
                  </p>
                </div>
              </Card>
              <Card
                className="pd-0 w-20 curs-point clicking-animation animated fadeInLeft delay-05s"
                onClick={() => {
                  EventManager.send(EventNames.EVENTS_NAME.DOCS.HOME_PAGE.IOS);
                  ChangeHistory("push", ROUTE_PATH.DOCS.SDK.IOS);
                }}
              >
                <div className="pd-20">
                  <Image src={AppleIcon} />
                  <p className="mr-t-20 fs-16 font-heavy">
                    {docsConstants.TITLES.IOS.TITLE}
                  </p>
                </div>
              </Card>
              <Card
                className="pd-0 w-20 curs-point clicking-animation animated fadeInLeft delay-01s"
                onClick={() => {
                  EventManager.send(
                    EventNames.EVENTS_NAME.DOCS.HOME_PAGE.JAVASCRIPT
                  );
                  ChangeHistory("push", ROUTE_PATH.DOCS.SDK.JAVASCRIPT);
                }}
              >
                <div className="pd-20">
                  <Image src={JavaIcon} />
                  <p className="mr-t-20 fs-16 font-heavy">
                    {docsConstants.TITLES.JAVASCRIPT.TITLE}
                  </p>
                </div>
              </Card>
              <Card
                className="pd-0 w-20 curs-point clicking-animation animated fadeInLeft delay-01s"
                onClick={() => {
                  EventManager.send(
                    EventNames.EVENTS_NAME.DOCS.HOME_PAGE.FLUTTER
                  );
                  ChangeHistory("push", ROUTE_PATH.DOCS.SDK.FLUTTER);
                }}
              >
                <div className="pd-20">
                  <Image src={FlutterIcon} />
                  <p className="mr-t-20 fs-16 font-heavy">
                    {docsConstants.TITLES.FLUTTER.TITLE}
                  </p>
                </div>
              </Card>
            </FlexBox>
          </div>
        }
        titleClass="fs-24 font-heavy mr-t-60"
        className="animated fadeInUp delay-03s"
      />
    </div>
  );

  const SdkSection = (
    <div ref={SdkSectionRef}>
      <TitleBlock
        title={docsConstants.TITLES.BACKEND_APIS}
        content={
          <div className="sdk-content">
            <p className="fs-16">
              OTPless’s direct API integration considers both developer and user
              experience to ensure fast, safe, and easy integration flows. Check
              out our{" "}
              <a
                href={ROUTE_PATH.DOCS.API_REFERENCE}
                target="_self"
                className="underline curs-point font-heavy"
                onClick={() => {
                  EventManager.send(
                    EventNames.EVENTS_NAME.DOCS.HOME_PAGE.REST_APIS
                  );
                }}
              >
                API reference
              </a>{" "}
              for more details.
            </p>
          </div>
        }
        titleClass="fs-24 font-heavy"
        className="mr-t-60 animated fadeInUp delay-02s"
      />
    </div>
  );

  //Return UI
  return (
    <DocsScreen
      className="docs-home-screen"
      hasSideMenu
      hasHeader
      screenAnimationClass="no-animation"
      tabSelectCallback={scrollToSpecificElement}
      scrolledToElement={{ ...scrolledToElement }}
    >
      {WelcomeSection}
      {GetStartedSection}
      {SdkSection}
      {SdkList}
    </DocsScreen>
  );
};

export default DocsHome;
